import { Link } from "@tanstack/react-router";
import { ArrowRight, Clock, BookOpen, Flame } from "lucide-react";
import { BLOG_POSTS, getLocalizedPosts } from "@/lib/mock/blog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useI18n } from "@/context/I18nContext";

interface BlogWidgetProps {
  title?: string;
  eyebrow?: string;
  limit?: number;
  category?: string;
  slugs?: string[];
  variant?: "grid" | "compact";
}

// Product category to Aqua MAG blog category mapping
const PRODUCT_CATEGORY_MAP: Record<string, string[]> = {
  "feed-additives": ["feed-innovation-sustainability-8"],
  "live-feed-aquaculture": ["feed-innovation-sustainability-8", "hatchery-feed-design-6"],
  "hatchery-solutions": ["aquaculture-10", "hatchery-feed-design-6"],
  seafood: ["aquaculture-10", "projects-4"],
};

export function BlogWidget({
  title = "Aqua MAG — Latest Technical Reports",
  eyebrow = "Journal & Research",
  limit = 3,
  category,
  slugs,
  variant = "grid",
}: BlogWidgetProps) {
  const { lang } = useI18n();
  const localizedPosts = getLocalizedPosts(lang);

  let filtered = localizedPosts;

  if (slugs && slugs.length > 0) {
    filtered = slugs
      .map((slug) => localizedPosts.find((p) => p.slug === slug))
      .filter((p): p is (typeof localizedPosts)[0] => p !== undefined);
  } else if (category) {
    const matchingAquaMagCategories = PRODUCT_CATEGORY_MAP[category] || [category];
    filtered = localizedPosts.filter(
      (p) => p.categorySlug === category || matchingAquaMagCategories.includes(p.categorySlug),
    );
  }

  // Sort by date descending so the newest, highest-value technical articles appear first
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  const posts = sorted.slice(0, limit);

  if (!posts.length) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-4 border-b border-border/60">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/20 text-navy text-xs font-bold uppercase tracking-widest mb-2">
            <BookOpen className="h-3.5 w-3.5 text-mint-ink" /> {eyebrow}
          </div>
          <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">{title}</h2>
        </div>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:text-navy transition-colors"
        >
          Explore Aqua MAG Library <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div
        className={
          variant === "compact" ? "grid gap-4 md:grid-cols-3" : "grid gap-6 md:grid-cols-3"
        }
      >
        {posts.map((post) => (
          <Link
            key={post.slug}
            to="/blog/$category/$slug"
            params={{ category: post.categorySlug, slug: post.slug }}
            className="glass-card rounded-2xl overflow-hidden border border-border/80 bg-background shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between group"
          >
            <div>
              {post.image && (
                <div className="aspect-[16/10] overflow-hidden bg-muted relative">
                  <img
                    src={post.image}
                    alt={post.title}
                    width={640}
                    height={400}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg bg-navy/80 text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur-md">
                    {post.category}
                  </div>
                </div>
              )}

              <div className="p-5 space-y-2">
                <h3 className="font-display text-base font-bold leading-snug text-navy group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">
                  {post.excerpt}
                </p>
              </div>
            </div>

            <div className="p-5 pt-0 flex items-center justify-between text-xs text-muted-foreground border-t border-border/40 mt-4">
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="bg-navy text-[10px] font-bold text-white">
                    {post.authorInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-navy text-[11px]">{post.author}</span>
              </div>
              <span className="flex items-center gap-1 font-semibold text-primary text-[11px]">
                <Clock className="h-3 w-3" /> {post.readMinutes} min
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
